import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { ShopifyVariant } from '../types';

/** Shared icon "chip" — gold-dim square with a gold icon, centered. Inline so
 *  it survives any CSS purge/cache mismatch (sizing + alignment must hold). */
const ICON_BOX: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 38,
  height: 38,
  borderRadius: 10,
  flexShrink: 0,
  background: 'rgba(168,118,62,0.12)',
  color: '#a8763e',
};

interface Props {
  variants: ShopifyVariant[];
  currency: string;
  /** Fired when a format's CTA is clicked — adds to cart + goes to checkout. */
  onOrder: (variant: ShopifyVariant) => void;
  /** True while an order is being processed — disables every CTA so it can
   *  only be clicked once (prevents double add-to-cart before the redirect). */
  busy?: boolean;
  /** The variant currently being ordered — its CTA shows the spinner. */
  busyVariantId?: number | null;
}

const POPULAR_PACKET = 'Instant Masterpiece';

type IconKey = 'download' | 'print' | 'canvas';

interface PacketMeta {
  icon: IconKey;
  desc: string;
  features: string[];
  cta: string;
  popular: boolean;
  countdown: boolean;
}

/**
 * FormatAccordion — the reference "Choose Your Format" card.
 *
 * One card per packet (grouped by option1), each with an icon, short
 * description, strikethrough + sale price, a feature checklist, an optional
 * urgency countdown, and its own CTA. Prints/canvas show a size dropdown
 * (option2). Matches template-woo bf-format-accordion / bf-acc-item.
 */
export function FormatAccordion({ variants, currency, onOrder, busy = false, busyVariantId = null }: Props) {
  const groups = useMemo(() => groupByOption1(variants), [variants]);
  const [sizePick, setSizePick] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const [name, vs] of groups) initial[name] = vs[0].id;
    return initial;
  });

  if (variants.length === 0) {
    return (
      <div className="p-6 rounded-[18px] bg-white border border-soft-border shadow-soft">
        <p className="text-sm text-mute">No purchase formats available for this product.</p>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 rounded-[18px] bg-white border border-soft-border shadow-soft">
      <header className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span aria-hidden="true" style={ICON_BOX}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
            <rect x="2" y="4" width="20" height="16" rx="2" />
          </svg>
        </span>
        <h2 className="font-sans font-bold text-warm-brown m-0" style={{ fontSize: 20, lineHeight: 1.2 }}>
          Choose Your Format
        </h2>
      </header>

      <div className="flex flex-col gap-4">
        {[...groups.entries()].map(([packetName, packetVariants]) => {
          const pickedId = sizePick[packetName];
          const picked = packetVariants.find((v) => v.id === pickedId) ?? packetVariants[0];
          const meta = metaFor(packetName);
          const hasSizes = packetVariants.length > 1 && packetVariants.some((v) => v.option2);

          return (
            <div
              key={packetName}
              data-packet={packetName}
              className={[
                'bf-fmt-card relative rounded-[14px] p-4 pt-5 border',
                meta.popular ? 'border-teal/45 bg-teal-dim/50' : 'border-soft-border bg-[#7f63491a]',
              ].join(' ')}
            >
              {meta.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-teal text-white text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-1 shadow-soft">
                  Most Popular
                </span>
              )}

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <span aria-hidden="true" style={ICON_BOX}>
                    <PacketIcon icon={meta.icon} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-warm-brown leading-tight">{packetName}</div>
                    <div className="text-xs text-warm-brown/70 mt-0.5">{meta.desc}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {picked.compare_at_price && picked.compare_at_price > picked.price && (
                    <div className="text-xs text-warm-brown/50 line-through leading-none">
                      {formatMoney(picked.compare_at_price, currency)}
                    </div>
                  )}
                  <div className="font-sans text-xl font-bold text-teal leading-tight mt-0.5">
                    {formatMoney(picked.price, currency)}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="mt-3 flex flex-col gap-3">
                {meta.countdown && <Countdown />}

                {hasSizes && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <label htmlFor={`size-${packetName}`} className="text-xs text-warm-brown/70">Size</label>
                    <select
                      id={`size-${packetName}`}
                      value={pickedId}
                      onChange={(e) => setSizePick((prev) => ({ ...prev, [packetName]: Number(e.target.value) }))}
                      className="px-2.5 py-1.5 rounded-[10px] border border-soft-border bg-white text-teal text-sm focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
                    >
                      {packetVariants.map((v) => (
                        <option key={v.id} value={v.id}>{v.option2 || v.option1}</option>
                      ))}
                    </select>
                  </div>
                )}

                <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-warm-brown">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-teal flex-shrink-0" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => onOrder(picked)}
                  disabled={busy}
                  aria-busy={busy && busyVariantId === picked.id}
                  className={[
                    'w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[10px] bg-teal text-white font-semibold text-sm shadow-soft transition-all active:scale-[0.98]',
                    busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-teal-dark',
                  ].join(' ')}
                >
                  {busy && busyVariantId === picked.id ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                      Processing…
                    </>
                  ) : (
                    meta.cta
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Urgency countdown — purely cosmetic, resets each page load ──────────────

const COUNTDOWN_START = 20 * 60; // 20:00

function Countdown() {
  const [secs, setSecs] = useState(COUNTDOWN_START);
  useEffect(() => {
    if (secs <= 0) return;
    const t = window.setTimeout(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secs]);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return (
    <p className="text-xs text-warm-brown m-0">
      Expires in{' '}
      <span className="font-semibold text-teal-dark tabular-nums">
        {m}:{String(s).padStart(2, '0')}
      </span>
    </p>
  );
}

function PacketIcon({ icon }: { icon: IconKey }) {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (icon === 'print') {
    return (
      <svg {...common}>
        <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </svg>
    );
  }
  if (icon === 'canvas') {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="18" height="18" rx="1" />
        <path d="M3 15l5-5 4 4 3-3 6 6" />
        <circle cx="9" cy="9" r="1.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

function metaFor(name: string): PacketMeta {
  const n = name.toLowerCase();
  if (name === POPULAR_PACKET || n.includes('instant') || n.includes('digital')) {
    return {
      icon: 'download',
      desc: 'Instant high-resolution download — perfect for sharing or saving.',
      features: ['No Watermark', 'Instant Download', 'High-Resolution Portrait'],
      cta: 'Download Now',
      popular: true,
      countdown: true,
    };
  }
  if (n.includes('canvas')) {
    return {
      icon: 'canvas',
      desc: 'Gallery-wrapped canvas, ready to hang.',
      features: ['Gallery-wrapped & hand-stretched', 'Ready to hang', 'Ships worldwide'],
      cta: 'Order Canvas',
      popular: false,
      countdown: false,
    };
  }
  return {
    icon: 'print',
    desc: 'Printed on museum-quality archival paper.',
    features: ['Museum-quality archival paper', 'Ready to frame', 'Ships worldwide'],
    cta: 'Order Print',
    popular: false,
    countdown: false,
  };
}

function groupByOption1(variants: ShopifyVariant[]): Map<string, ShopifyVariant[]> {
  const out = new Map<string, ShopifyVariant[]>();
  for (const v of variants) {
    const key = v.option1 || 'Other';
    // Hide the "Framed Canvas Print" format on the preview page (matches the
    // reference brand, which only offers digital + plain canvas). Exact name
    // match only (2026-10-15 fix) — the old substring check on "framed" also
    // caught a differently-named format ("Framed Poster") that should show.
    if (key.toLowerCase() === 'framed canvas print') continue;
    const bucket = out.get(key);
    if (bucket) bucket.push(v);
    else out.set(key, [v]);
  }
  return out;
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
